import React from 'react';

import { keyframes } from '@emotion/react';
import { Check, Eye, LoaderCircle, SquareCheck, TriangleAlert, X } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import type { PlaybookTone } from './playbook-colors';
import { PLAYBOOK_TONES } from './playbook-colors';
import type { PlaybookRuleStatus, PlaybookStatus } from './playbook-run-state';

export type PlaybookChipTone =
  | 'failed'
  | 'muted-failed'
  | 'muted-passed'
  | 'neutral'
  | 'not-run'
  | 'passed'
  | 'resolved'
  | 'review'
  | 'reviewing'
  | 'running';

/**
 * One tone → colors mapping for every status chip, filter dot, and tinted
 * control in the panel. Ten chips, seven tones: an outcome and its muted twin
 * read the same way, and a rule nobody has run yet is the same absence of a
 * status as a count.
 */
export const PLAYBOOK_TONE_COLORS = {
  failed: PLAYBOOK_TONES.failed,
  'muted-failed': PLAYBOOK_TONES.muted,
  'muted-passed': PLAYBOOK_TONES.muted,
  neutral: PLAYBOOK_TONES.neutral,
  'not-run': PLAYBOOK_TONES.neutral,
  passed: PLAYBOOK_TONES.passed,
  resolved: PLAYBOOK_TONES.passed,
  review: PLAYBOOK_TONES.review,
  // A position mid-review: in progress like running, but user-driven.
  reviewing: PLAYBOOK_TONES.reviewing,
  running: PLAYBOOK_TONES.running,
} as const satisfies Record<PlaybookChipTone, PlaybookTone>;

export const PLAYBOOK_CHIP_LABELS = {
  accepted: 'Accepted',
  couldntEvaluate: 'Couldn’t Evaluate',
  editsAdded: 'Edits Added',
  editsApplied: 'Edits Applied',
  failedToRun: 'Failed To Run',
  met: 'Met',
  needsReview: 'Needs Review',
  noRules: 'No Rules',
  notMet: 'Not Met',
  notRun: 'Not Run',
  passed: 'Passed',
  resolved: 'Resolved',
  reviewed: 'Reviewed',
  reviewing: 'Reviewing',
  running: 'Running',
} as const;

export const PLAYBOOK_STATUS_CHIPS = {
  'not-run': { label: PLAYBOOK_CHIP_LABELS.notRun, tone: 'not-run' },
  passed: { label: PLAYBOOK_CHIP_LABELS.passed, tone: 'passed' },
  resolved: { label: PLAYBOOK_CHIP_LABELS.resolved, tone: 'resolved' },
  review: { label: PLAYBOOK_CHIP_LABELS.needsReview, tone: 'review' },
  reviewing: { label: PLAYBOOK_CHIP_LABELS.reviewing, tone: 'reviewing' },
  running: { label: PLAYBOOK_CHIP_LABELS.running, tone: 'running' },
} as const satisfies Record<PlaybookStatus, { label: string; tone: PlaybookChipTone }>;

export const PLAYBOOK_RULE_STATUS_CHIPS = {
  failed: { label: PLAYBOOK_CHIP_LABELS.failedToRun, tone: 'failed' },
  ...PLAYBOOK_STATUS_CHIPS,
} as const satisfies Record<PlaybookRuleStatus, { label: string; tone: PlaybookChipTone }>;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const useStyles = makeStyles<{ tone: PlaybookChipTone }>()((theme, { tone }) => {
  const toneStyle = PLAYBOOK_TONE_COLORS[tone];

  return {
    /**
     * Compact by design: chips annotate a heading rather than sit beside it as
     * equals, so the type size holds at 12 and the box tightens around it.
     */
    chip: {
      alignItems: 'center',
      backgroundColor: toneStyle.background,
      borderColor: toneStyle.border,
      borderRadius: 999,
      borderStyle: 'solid',
      borderWidth: 1,
      color: toneStyle.color,
      display: 'inline-flex',
      fontSize: 12,
      fontVariantNumeric: 'tabular-nums',
      fontWeight: 500,
      gap: theme.spacing(0.375),
      lineHeight: '16px',
      padding: theme.spacing(0, 0.75),
      whiteSpace: 'nowrap',
    },
    icon: {
      flexShrink: 0,
      height: 10,
      strokeWidth: 2.5,
      width: 10,
    },
    spinning: {
      animation: `${spin} 1s linear infinite`,
    },
  };
});

function ChipIcon({ tone }: { tone: PlaybookChipTone }): JSX.Element | null {
  const { classes, cx } = useStyles({ tone });
  switch (tone) {
    // No outcome, no mark: Not Run and counts carry no icon.
    case 'neutral':
    case 'not-run':
      return null;
    case 'running':
      return <LoaderCircle className={cx(classes.icon, classes.spinning)} />;
    case 'muted-passed':
    case 'passed':
      return <Check className={classes.icon} />;
    case 'review':
      return <TriangleAlert className={classes.icon} />;
    case 'reviewing':
      return <Eye className={classes.icon} />;
    case 'failed':
    case 'muted-failed':
      return <X className={classes.icon} />;
    case 'resolved':
      return <SquareCheck className={classes.icon} />;
  }
}

export function PlaybookChip({
  label,
  tone,
}: {
  label: string;
  tone: PlaybookChipTone;
}): JSX.Element {
  const { classes } = useStyles({ tone });

  return (
    <span className={classes.chip}>
      <ChipIcon tone={tone} />
      {label}
    </span>
  );
}

/** The playbook-level rollup chip, shared by the list card and the chat run card. */
export function PlaybookStatusChip({ status }: { status: PlaybookStatus }): JSX.Element {
  const { label, tone } = PLAYBOOK_STATUS_CHIPS[status];
  return <PlaybookChip label={label} tone={tone} />;
}
